import type { FileNode } from '@ncoder/ui';
import type { LoadChildrenFn } from '@ncoder/ui';
import { fetchRetrievalMetadataItems } from './useRetrievalMetadataItems';

interface CreateMetadataLoadChildrenOptions {
  retrievalId: string | null | undefined;
  pageSize?: number;
}

export function createMetadataLoadChildren({
  retrievalId,
  pageSize = 20,
}: CreateMetadataLoadChildrenOptions): LoadChildrenFn {
  return async (folder, options) => {
    if (!retrievalId || !folder.metadataType) {
      return { items: [] };
    }
    const data = await fetchRetrievalMetadataItems({
      retrievalId,
      metadataType: folder.metadataType,
      first: pageSize,
      after: options?.after ?? undefined,
    });

    let items: FileNode[];

    if (data.isBundleType) {
      items = (data.edges ?? []).map((edge) => ({
        id: `${folder.metadataType}/${edge.node.folderName}`,
        name: edge.node.folderName,
        type: 'folder' as const,
        path: `/${folder.metadataType}/${edge.node.folderName}`,
        metadataType: folder.metadataType,
        count: edge.node.itemCount,
        children: edge.node.items.map((item) => ({
          id: item.id,
          name: item.fileName ?? item.fullName,
          type: 'file' as const,
          path: `/${item.metadataType}/${item.fullName}`,
          metadataType: item.metadataType,
        })),
      }));
    } else {
      const mapped = (data.edges ?? []).map((edge) => {
        const file = edge.node.items[0];
        if (!file) return null;
        const node: FileNode = {
          id: file.id,
          name: file.fileName ?? file.fullName,
          type: 'file',
          path: `/${file.metadataType}/${file.fullName}`,
          metadataType: file.metadataType,
        };
        return node;
      });
      items = mapped.filter((item): item is FileNode => item !== null);
    }

    return {
      items,
      nextCursor: data.pageInfo?.endCursor ?? null,
      hasNextPage: data.pageInfo?.hasNextPage ?? false,
    };
  };
}
